import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react"

import { useAppState } from "../../context/appStateContext"
import native from "../../native/native"
import { formatCurrency } from "../../utils/utils"
import styles from "./SightlinePaywall.module.scss"

// Everyday camera-roll memories, reused from the bundled photo set so the
// screen renders fully self-contained (no external fetches at runtime).
const PHOTOS: { file: string; alt: string }[] = [
  { file: "coffee-1.jpg", alt: "morning coffee" },
  { file: "dog-2.jpg", alt: "our dog" },
  { file: "beach-1.jpg", alt: "the coast" },
  { file: "flower-2.jpg", alt: "flowers" },
  { file: "sunset-1.jpg", alt: "golden hour" },
  { file: "food-3.jpg", alt: "dinner out" },
  { file: "city-2.jpg", alt: "city night" },
  { file: "mountain-1.jpg", alt: "the hike" },
  { file: "dog-1.jpg", alt: "the puppy" },
  { file: "coffee-3.jpg", alt: "cafe" },
  { file: "beach-3.jpg", alt: "the waves" },
  { file: "sunset-3.jpg", alt: "sunset drive" },
  { file: "flower-1.jpg", alt: "spring" },
  { file: "food-1.jpg", alt: "breakfast" },
  { file: "mountain-3.jpg", alt: "the summit" },
  { file: "city-1.jpg", alt: "downtown" },
  { file: "dog-3.jpg", alt: "walkies" },
  { file: "beach-2.jpg", alt: "the shore" },
  { file: "coffee-2.jpg", alt: "latte" },
  { file: "food-2.jpg", alt: "lunch" },
  { file: "sunset-2.jpg", alt: "dusk" },
  { file: "mountain-2.jpg", alt: "the trail" },
  { file: "flower-3.jpg", alt: "bloom" },
  { file: "city-3.jpg", alt: "skyline" },
]

// A handful of tiles get a taller footprint so the grid reads like a real,
// mixed camera roll rather than a uniform matrix.
const TALL = new Set([1, 6, 10, 14, 19, 21])

const LockGlyph = ({ className }: { className?: string }) => (
  <svg
    className={className}
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <rect x="4.5" y="10.5" width="15" height="11" rx="3" fill="currentColor" />
    <path
      d="M8 10.5V7.5a4 4 0 0 1 8 0v3"
      stroke="currentColor"
      strokeWidth="2.2"
      strokeLinecap="round"
      fill="none"
    />
  </svg>
)

const EyeGlyph = ({ className }: { className?: string }) => (
  <svg
    className={className}
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <path
      d="M2 12s3.8-6.5 10-6.5S22 12 22 12s-3.8 6.5-10 6.5S2 12 2 12Z"
      stroke="currentColor"
      strokeWidth="1.9"
      fill="none"
    />
    <circle cx="12" cy="12" r="2.6" fill="currentColor" />
  </svg>
)

const GrabGlyph = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M9 7 5 12l4 5M15 7l4 5-4 5"
      stroke="currentColor"
      strokeWidth="2.2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

const Grid = ({ variant }: { variant: "exposed" | "vault" }) => (
  <div
    className={`${styles.grid} ${
      variant === "vault" ? styles.gridVault : styles.gridExposed
    }`}
    aria-hidden={variant === "vault"}
  >
    {PHOTOS.map((p, i) => (
      <div
        key={p.file}
        className={`${styles.tile} ${TALL.has(i) ? styles.tall : ""}`}
      >
        <img
          src={`${process.env.PUBLIC_URL}/recall/${p.file}`}
          alt={variant === "exposed" ? p.alt : ""}
          loading="eager"
          draggable={false}
        />
        {variant === "vault" && i % 5 === 2 && (
          <span className={styles.keptTick} aria-hidden="true">
            <svg viewBox="0 0 24 24" fill="none">
              <path
                d="m5 12.5 4.5 4.5L19 7"
                stroke="currentColor"
                strokeWidth="3"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </span>
        )}
      </div>
    ))}
  </div>
)

// Slider position expressed as % of the stage width that belongs to the vault
// (measured from the left edge). Higher = more memories pulled private.
const MIN = 8
const MAX = 100
const REST = 58

const SightlinePaywall = () => {
  const { state, actions } = useAppState()

  const premium = state.productData?.premium
  const yearlyProduct = premium?.yearlyProduct
  const monthlyProduct = premium?.monthlyProduct
  const locale = yearlyProduct?.priceLocale

  const yearlyPrice = yearlyProduct
    ? formatCurrency(yearlyProduct.price, locale)
    : "$35.99"
  const yearlyPerMonth = yearlyProduct
    ? formatCurrency(yearlyProduct.price / 12, locale)
    : "$2.99"
  const monthlyPrice = monthlyProduct
    ? formatCurrency(monthlyProduct.price, locale)
    : "$13.99"
  const hasTrial = yearlyProduct?.hasTrial ?? true
  const trialDays = yearlyProduct?.introPricing?.period ?? 7
  const savePercent =
    yearlyProduct && monthlyProduct && monthlyProduct.price > 0
      ? Math.max(
          0,
          Math.round(
            (1 - yearlyProduct.price / (monthlyProduct.price * 12)) * 100,
          ),
        )
      : 79

  const topInset = state.initialData?.topInset ?? 44
  const bottomInset = state.initialData?.bottomInset ?? 20

  const [selected, setSelected] = useState<"yearly" | "monthly">("yearly")
  const [pos, setPos] = useState(MIN)
  const [sealed, setSealed] = useState(false)

  const stageRef = useRef<HTMLDivElement>(null)
  const draggingRef = useRef(false)
  const introRef = useRef<number | null>(null)
  const startedRef = useRef(false)

  // Tint the OS inset bars to blend with the screen background.
  useEffect(() => {
    const bg = state.initialData?.darkMode ? "#171b24" : "#f4efe7"
    actions.setHeaderInsetColor(bg)
    actions.setFooterInsetColor(
      state.initialData?.darkMode ? "#12151d" : "#ffffff",
    )
  }, [actions, state.initialData?.darkMode])

  // One-time teaching sweep: glide the seam open so the interaction is obvious,
  // then settle at REST inviting the user to take over.
  useLayoutEffect(() => {
    if (startedRef.current) return
    startedRef.current = true
    const duration = 2100
    let raf = 0
    let startTs = 0
    const ease = (t: number) => 1 - Math.pow(1 - t, 3)
    const tick = (ts: number) => {
      if (!startTs) startTs = ts
      const t = Math.min(1, (ts - startTs) / duration)
      // MIN -> 78 -> REST, a gentle overshoot that shows both extremes.
      const eased = ease(t)
      const peak = 78
      const value =
        eased < 0.62
          ? MIN + ((peak - MIN) * eased) / 0.62
          : peak - ((peak - REST) * (eased - 0.62)) / 0.38
      if (!draggingRef.current) setPos(value)
      if (t < 1 && !draggingRef.current) raf = requestAnimationFrame(tick)
    }
    const kickoff = window.setTimeout(() => {
      raf = requestAnimationFrame(tick)
    }, 420)
    introRef.current = kickoff
    return () => {
      window.clearTimeout(kickoff)
      cancelAnimationFrame(raf)
    }
  }, [])

  const applyFromClientX = useCallback((clientX: number) => {
    const el = stageRef.current
    if (!el) return
    const rect = el.getBoundingClientRect()
    const pct = ((clientX - rect.left) / rect.width) * 100
    const clamped = Math.max(MIN, Math.min(MAX, pct))
    setPos(clamped)
    setSealed(clamped >= 99.4)
  }, [])

  const onPointerDown = useCallback(
    (e: React.PointerEvent) => {
      draggingRef.current = true
      if (introRef.current) window.clearTimeout(introRef.current)
      ;(e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId)
      applyFromClientX(e.clientX)
    },
    [applyFromClientX],
  )

  const onPointerMove = useCallback(
    (e: React.PointerEvent) => {
      if (!draggingRef.current) return
      applyFromClientX(e.clientX)
    },
    [applyFromClientX],
  )

  const endDrag = useCallback(() => {
    draggingRef.current = false
  }, [])

  const onKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (introRef.current) window.clearTimeout(introRef.current)
    if (e.key === "ArrowRight" || e.key === "ArrowUp") {
      setPos((p) => {
        const n = Math.min(MAX, p + 6)
        setSealed(n >= 99.4)
        return n
      })
      e.preventDefault()
    } else if (e.key === "ArrowLeft" || e.key === "ArrowDown") {
      setPos((p) => {
        const n = Math.max(MIN, p - 6)
        setSealed(n >= 99.4)
        return n
      })
      e.preventDefault()
    }
  }, [])

  const securedCount = useMemo(() => {
    // Photos whose column falls left of the seam are "on your side".
    const total = PHOTOS.length
    return Math.max(1, Math.round((pos / 100) * total))
  }, [pos])

  const buy = useCallback(() => {
    const product = selected === "yearly" ? yearlyProduct : monthlyProduct
    if (product) native.subscribe(product)
  }, [selected, yearlyProduct, monthlyProduct])

  const close = useCallback(() => native.postNativeEvent("close"), [])

  const ctaLabel =
    selected === "yearly" && hasTrial
      ? `Try ${trialDays} days free`
      : "Continue"

  return (
    <div
      className={`${styles.page} ${sealed ? styles.isSealed : ""}`}
      style={{ paddingTop: topInset }}
    >
      <div className={styles.grain} aria-hidden="true" />

      <button
        className={styles.close}
        onClick={close}
        aria-label="Not now"
        style={{ top: topInset + 4 }}
      >
        <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path
            d="m6 6 12 12M18 6 6 18"
            stroke="currentColor"
            strokeWidth="2.2"
            strokeLinecap="round"
          />
        </svg>
      </button>

      <header className={styles.intro}>
        <span className={styles.kicker}>
          <LockGlyph className={styles.kickerLock} />
          Private Photo Vault
        </span>
        <h1 className={styles.headline}>
          Draw the line between{" "}
          <span className={styles.headlineAnyone}>anyone</span> and{" "}
          <span className={styles.headlineYou}>only you</span>.
        </h1>
      </header>

      <section
        ref={stageRef}
        className={styles.stage}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
        role="slider"
        aria-label="Drag to move memories into your private vault"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={Math.round(pos)}
        tabIndex={0}
        onKeyDown={onKeyDown}
      >
        {/* Base plane: everything, exposed. */}
        <div className={styles.plane}>
          <Grid variant="exposed" />
          <div className={styles.exposedWash} />
        </div>

        {/* Overlay plane: the same grid, but private — clipped to the vault side. */}
        <div
          className={styles.plane}
          style={{ clipPath: `inset(0 ${100 - pos}% 0 0)` }}
        >
          <Grid variant="vault" />
          <div className={styles.vaultWash} />
        </div>

        {/* Side tags */}
        <div
          className={styles.tagVault}
          style={{ opacity: pos < 22 ? 0.35 : 1 }}
        >
          <LockGlyph className={styles.tagIcon} />
          Only you
        </div>
        <div
          className={styles.tagExposed}
          style={{ opacity: pos >= 98 ? 0 : pos > 84 ? 0.25 : 1 }}
        >
          <EyeGlyph className={styles.tagIcon} />
          Anyone with your phone
        </div>

        {/* Live count riding just inside the vault side of the seam */}
        <div className={styles.counter} style={{ left: `${pos}%` }}>
          <LockGlyph className={styles.counterLock} />
          <strong>{securedCount}</strong>
          <span>secured</span>
        </div>

        {/* The seam + handle */}
        <div className={styles.seam} style={{ left: `${pos}%` }}>
          <div className={styles.seamLine} />
          <div className={styles.handle}>
            {sealed ? (
              <LockGlyph className={styles.handleIcon} />
            ) : (
              <GrabGlyph className={styles.handleIcon} />
            )}
          </div>
        </div>

        <div className={styles.hint} style={{ opacity: pos > 90 ? 0 : 1 }}>
          Drag →
        </div>
      </section>

      <section
        className={styles.pricing}
        style={{ paddingBottom: bottomInset + 14 }}
      >
        <p className={styles.pitch}>
          {sealed
            ? "Sealed. Every memory stays on your side — locked to only you."
            : "Keep every memory on your side of the line."}
        </p>

        <div className={styles.plans}>
          <button
            className={`${styles.planYear} ${
              selected === "yearly" ? styles.planActive : ""
            }`}
            onClick={() => setSelected("yearly")}
            aria-pressed={selected === "yearly"}
          >
            <span className={styles.planTop}>
              <span className={styles.planName}>Yearly</span>
              <span className={styles.saveBadge}>SAVE {savePercent}%</span>
            </span>
            <span className={styles.planPriceRow}>
              <span className={styles.planPrice}>{yearlyPrice}</span>
              <span className={styles.planPer}>/year</span>
            </span>
            <span className={styles.planSub}>
              {yearlyPerMonth}/mo, billed yearly
              {hasTrial ? ` · ${trialDays}-day free trial` : ""}
            </span>
            <span className={styles.radio} aria-hidden="true" />
          </button>

          <button
            className={`${styles.planMonth} ${
              selected === "monthly" ? styles.planMonthActive : ""
            }`}
            onClick={() => setSelected("monthly")}
            aria-pressed={selected === "monthly"}
          >
            Prefer monthly? <strong>{monthlyPrice}/mo</strong>
          </button>
        </div>

        <button className={styles.cta} onClick={buy}>
          <span>{ctaLabel}</span>
          <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path
              d="M5 12h13m0 0-5-5m5 5-5 5"
              stroke="currentColor"
              strokeWidth="2.2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>

        <p className={styles.fine}>
          {selected === "yearly" && hasTrial
            ? `${trialDays} days free, then ${yearlyPrice}/year. `
            : selected === "yearly"
              ? `${yearlyPrice}/year. `
              : `${monthlyPrice}/month. `}
          Auto-renews until canceled.
        </p>

        <div className={styles.legal}>
          <button onClick={() => native.postNativeEvent("already_purchased")}>
            Restore
          </button>
          <span aria-hidden="true">·</span>
          <button onClick={() => native.postNativeEvent("terms")}>Terms</button>
          <span aria-hidden="true">·</span>
          <button onClick={() => native.postNativeEvent("privacy")}>
            Privacy
          </button>
        </div>
      </section>
    </div>
  )
}

export default SightlinePaywall
